import type { MessageUiState } from '@elara/client-core/states/MessageUiState';
import type { MessagePart, ReasoningPart } from '@elara/domain/models/schemas/message-schemas';

import React from 'react';

import { resolveToolBadgeGroup } from '@elara/client-core/chat/tool-badges/group-view-model';
import { parseReasoning } from '@elara/client-core/states/MessageUiState';
import { isAnsweredQuestionsPart } from '@elara/domain/models/ChatMessageModelSpec';
import {
  isReasoningPart,
  isTextPart,
  isToolRequestPart,
  isWidgetPart,
} from '@elara/domain/models/schemas/message-schemas';

import { useAppState, useUserProfileState } from '#states/hooks.js';
import { ToolBadge, ToolBadgeGroup } from '#ui/chat/tool-badges/index.js';
import { CollapsibleBlock } from '#ui/debug/collapsible-block.js';
import { useMessageRef } from '#ui/hooks/index.js';

import { AnsweredQuestionsBlock } from './AnsweredQuestionsBlock.js';
import { SimpleMarkdown } from './SimpleMarkdown.js';
import { renderWidgetFromPart } from './widget-renderer.js';

interface MessageContentRendererProps {
  content: MessagePart[];
  state?: MessageUiState;
  className?: string;
}

/**
 * Renders message content by processing parts sequentially.
 * Supports text parts (rendered as markdown) and widget parts (rendered as widgets).
 */
export function MessageContentRenderer({ content, state, className }: MessageContentRendererProps) {
  const { debugMode } = useAppState();
  const { isPrivileged } = useUserProfileState();
  const messageRef = useMessageRef();
  const parts: React.ReactNode[] = [];
  const isDebugView = debugMode && isPrivileged;

  if (!isDebugView) {
    const excludedIds = state?.latestInProgressToolCall ? new Set([state.latestInProgressToolCall.id]) : undefined;
    const group = resolveToolBadgeGroup(content, state?.toolCalls ?? [], excludedIds);
    if (group.toolCalls.length > 0) {
      parts.push(
        <ToolBadgeGroup key="tool-badge-group" icon={group.icon} toolCalls={group.toolCalls} summary={group.summary} />,
      );
    }
  }

  for (const [index, part] of content.entries()) {
    if (isTextPart(part)) {
      if (!part.text || part.text.trim() === '') continue;
      parts.push(<SimpleMarkdown key={`text-${index}`} text={part.text} />);
    } else if (isWidgetPart(part)) {
      const widgetElement = renderWidgetFromPart({ part, index, ctx: { messageRef } });
      if (widgetElement) parts.push(widgetElement);
      else console.error('[MessageContentRenderer] Unknown widget part', part);
    } else if (isReasoningPart(part) && debugMode) {
      // Skip partial/malformed reasoning — ReasoningBlock would render null and
      // we don't want the outer div to report it as visible content.
      if (!parseReasoning(part.reasoning)) continue;
      parts.push(<ReasoningBlock key={`reasoning-${index}`} part={part} />);
    } else if (isAnsweredQuestionsPart(part)) {
      const answers = part.data.answeredQuestions;
      if (!answers || Object.keys(answers).length === 0) continue;
      parts.push(<AnsweredQuestionsBlock key={`answered-questions-${index}`} part={part} />);
    } else if (isToolRequestPart(part)) {
      if (!isDebugView) continue;
      const toolCall = state?.toolCalls?.find((tc) => tc.id === part.toolRequest.ref);
      if (!toolCall) continue;
      if (state?.latestInProgressToolCall?.id === toolCall.id) continue;
      parts.push(<ToolBadge key={`tool-request-${index}`} toolCall={toolCall} variant={'square'} className="w-full" />);
    }
  }

  if (parts.length === 0) return null;

  return (
    <div
      className={`prose prose-sm dark:prose-invert max-w-full break-words markdown-content space-y-4 [&>div:first-child]:mt-0 ${className || ''}`}
    >
      {parts}
    </div>
  );
}

function ReasoningBlock({ part }: { part: ReasoningPart }) {
  const parsed = parseReasoning(part.reasoning);
  // Skip rendering if reasoning doesn't match expected format
  if (!parsed) return null;
  return (
    <CollapsibleBlock
      icon="neurology"
      label={parsed.subject}
      className="bg-muted/40 text-muted-foreground/50 border-0 p-2 my-3"
      labelClassName="overflow-hidden text-ellipsis whitespace-nowrap"
    >
      <SimpleMarkdown text={parsed.thought} />
    </CollapsibleBlock>
  );
}
